import React from 'react';
import Header from './Header';
import Footer from './Footer';
import Animation from '../animation/animation';
import { NoiseBackground } from './ui/noise-background';
import { CardContainer, CardBody, CardItem } from './ui/3d-card';
import { RunningBorder } from './ui/running-border';
import { InfiniteMovingCards } from './ui/infinite-moving-cards';
import { motion } from 'framer-motion';

const mainSponsors = [
  {
    name: "Hackculture",
    type: "Platform Partner",
    description: "Hackculture is our dedicated platform partner, providing the infrastructure and support to host our innovative hackathon and connect developers worldwide.",
    tier: "platform",
    color: "from-blue-500 to-cyan-400",
    image: "/sponsors/Hackculture.PNG"
  },
  {
    name: "Genesis",
    type: "Co-Host",
    description: "Genesis is our Co-Host, working alongside us to create a vibrant ecosystem for developers and innovators.",
    tier: "genesis",
    color: "from-[#8B5CF6] to-[#D946EF]",
    image: "/sponsors/Genesis.png"
  },
  {
    name: "GitHub",
    type: "Gold Sponsor",
    description: "GitHub is the world's leading AI-powered developer platform to build, scale, and deliver secure software.",
    tier: "gold",
    color: "from-yellow-400 to-yellow-600",
    image: "/sponsors/Github.png"
  },
  {
    name: "PUNJAB NATIONAL BANK",
    type: "Silver Sponsor",
    description: "Punjab National Bank (PNB) is one of India's leading public sector banks, supporting innovation and financial growth across the nation.",
    tier: "silver",
    color: "from-gray-300 to-gray-500",
    image: "/sponsors/PNB.png"
  },
  {
    name: "RISE IN",
    type: "Bronze Sponsor",
    description: "Rise In is a platform dedicated to empowering developers in the Web3 space, specifically focusing on the Stellar ecosystem.",
    tier: "bronze",
    color: "from-[#E61933] to-[#FF33CC]",
    image: "/sponsors/Risein.PNG"
  },
  {
    name: "BRU",
    type: "Coffee Sponsor",
    description: "BRU is our official coffee partner, keeping our hackers energized and focused with the finest brew throughout the event.",
    tier: "coffee",
    color: "from-[#7B3F00] to-[#D2B48C]",
    image: "/sponsors/Bru.PNG"
  },
  {
    name: "buildit",
    type: "Sponsor",
    description: "Buildit is our official sponsor, empowering developers to create, innovate and build the next generation of digital solutions.",
    tier: "buildit",
    color: "from-[#064E3B] to-[#065F46]",
    image: "/sponsors/Buildit.png"
  }
];

const partnerSponsors = [];

const inKindSponsors = [
  { name: "eventopia", type: "Digital Media Partner", image: "/sponsors/eventopia.jpeg" },
  { name: "sk printing", type: "In-Kind Sponsor", image: "/sponsors/Sk printing.jpeg" },
  { name: "gfg", type: "In-Kind Sponsor", image: "/sponsors/GFG.jpeg" },
  { name: "posterly", type: "In-Kind Sponsor", image: "/sponsors/posterly.png" },
  { name: "appwrite", type: "In-Kind Sponsor", image: "/sponsors/appwrite.png" },
  { name: "Five minutes of beauty", type: "In-Kind Sponsor", image: "/sponsors/5 Mins of beauty.png" },
  { name: "Xyz", type: "In-Kind Sponsor", image: "/sponsors/xyz.png" },
  { name: "Interviewbuddy", type: "In-Kind Sponsor", image: "/sponsors/Interview buddy.jpeg" },
  { name: "Bindu", type: "In-Kind Sponsor", image: "/sponsors/Bindu.png" },
  { name: "resourcio", type: "In-Kind Sponsor", image: "/sponsors/resourcio.png" },
  { name: "Nivixpe", type: "In-Kind Sponsor", image: "/sponsors/Nivixpe.png" },
  { name: "Cornitos", type: "In-Kind Sponsor", image: "/sponsors/cornitos.png" }
];

// Community Partners
const communityPartners = [
  { name: "gdgiem", type: "Community Partner", image: "/sponsors/gdgiem.png" },
  { name: "hackinfinity", type: "Community Partner", image: "/sponsors/hackinfinity.png" },
  { name: "codechef", type: "Community Partner", image: "/sponsors/codechef.png" },
  { name: "codeverse", type: "Community Partner", image: "/sponsors/codeverse.png" },
  { name: "ieeegalgotias", type: "Community Partner", image: "/sponsors/ieeegalgotias.png" },
  { name: "skillseeker", type: "Community Partner", image: "/sponsors/skillseeker.png" },
  { name: "tanuibf", type: "Community Partner", image: "/sponsors/tanuibf.png" },
  { name: "devops", type: "Community Partner", image: "/sponsors/devops.png" },
];

const SponsorImage = ({ src, name, className }) => {
  const [imgSrc, setImgSrc] = React.useState(src);
  const [attempt, setAttempt] = React.useState(0);
  const [error, setError] = React.useState(false);

  const extensions = ['.png', '.jpg', '.jpeg', '.PNG'];

  const handleError = () => {
    if (!src || src.startsWith('http')) {
      setError(true);
      return;
    }

    if (attempt < extensions.length) {
      // Get the base path without extension
      const lastDotIndex = src.lastIndexOf('.');
      const basePath = lastDotIndex !== -1 ? src.substring(0, lastDotIndex) : src;
      
      setImgSrc(`${basePath}${extensions[attempt]}`);
      setAttempt(prev => prev + 1);
    } else {
      setError(true);
    }
  };

  if (error || !src) {
    return (
      <div className={`flex items-center justify-center bg-white/5 backdrop-blur-sm border border-white/10 ${className}`}>
        <span className="text-white font-bold uppercase tracking-tighter text-[10px] md:text-xs text-center px-1 break-all leading-tight">
          {name}
        </span>
      </div>
    );
  }

  return (
    <img 
      src={imgSrc} 
      alt={name} 
      className={`object-contain ${className}`}
      onError={handleError} 
      style={{ display: error ? 'none' : 'block' }}
    />
  );
};

const CommunityCard = ({ partner }) => (
  <div className="w-40 h-24 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-2 flex flex-col items-center justify-center hover:border-[#E61933]/50 hover:bg-white/10 transition-all duration-300 group">
    <span className="text-white/80 font-bold uppercase tracking-widest text-xs text-center px-2 group-hover:text-white transition-colors">
      {partner.name}
    </span>
  </div>
);

const SponsorsListPage = () => {
  return (
    <div className="sponsors-page relative overflow-x-hidden min-h-screen flex flex-col">
      <div className="fixed top-0 left-0 w-full h-screen -z-[1] pointer-events-none">
        <Animation className="w-full h-full" />
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.1) 55%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,1) 100%)'
          }}
        />
      </div>
      <Header />
      <main className="flex-grow relative z-10 w-full max-w-[1400px] mx-auto px-5 pt-40 flex flex-col items-center">
        {/* Hero Section */}
        <div className="w-full flex flex-col items-center justify-center min-h-[50vh] mb-24">
          <div className="w-full flex flex-col items-center justify-start pt-0 rounded-md">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="md:text-6xl text-4xl lg:text-6xl font-black text-center text-transparent bg-clip-text bg-gradient-to-r from-[#E61933] to-[#FF33CC] relative z-20 font-['Inter'] drop-shadow-[0_2px_10px_rgba(230,25,51,0.3)]"
            >
              Sponsors
            </motion.h1>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="w-[50rem] max-w-[90vw] h-20 relative mt-4"
            >
               {/* Gradients */}
               <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-[#E61933]/50 to-transparent h-[2px] w-3/4 blur-sm" />
               <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-[#E61933]/50 to-transparent h-px w-3/4" />
               <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-[#FF33CC]/30 to-transparent h-[5px] w-1/4 blur-sm" />
               <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-[#FF33CC]/30 to-transparent h-px w-1/4" />
            </motion.div>

          </div>
        </div>

        <div className="w-full pb-20">
          {/* Main Sponsors Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20 w-full mx-auto">
            {mainSponsors.map((sponsor, index) => {
              const borderColor = {
                platform: '#06B6D4', // cyan-500
                genesis: '#8B5CF6', // violet
                media: '#10B981', // emerald
                coffee: '#7B3F00', // brown
                buildit: '#064E3B', // dark green
                gold: '#EAB308', // yellow-500
                silver: '#9CA3AF', // gray-400
                bronze: '#E61933' // neon red
              }[sponsor.tier] || '#E61933';

              const isLastItem = index === mainSponsors.length - 1;
              const isOddTotal = mainSponsors.length % 2 !== 0;

              return (
                <div key={index} className={`${isLastItem && isOddTotal ? 'lg:col-span-2 flex justify-center' : ''}`}>
                  <CardContainer className={`inter-var h-full ${isLastItem && isOddTotal ? 'w-full lg:w-1/2' : 'w-full'}`} isStatic={true}>
                    <CardBody className="bg-black/40 backdrop-blur-xl relative group/card border-white/10 hover:border-white/20 w-full h-full rounded-xl p-8 border transition-all duration-300 flex flex-col">
                      <RunningBorder color={borderColor} reverse={index % 2 !== 0} />
                      <div className="flex justify-between items-start mb-6">
                      <CardItem
                        translateZ="0"
                        className="text-2xl font-bold text-white font-['Inter']"
                      >
                        {sponsor.name}
                      </CardItem>
                      <CardItem
                        translateZ="0"
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r ${sponsor.color} text-black shadow-lg`}
                      >
                        {sponsor.type}
                      </CardItem>
                    </div>
                    
                    <CardItem
                      as="p"
                      translateZ="0"
                      className="text-white/70 text-sm leading-relaxed font-['Poppins'] mb-8 flex-grow"
                    >
                      {sponsor.description}
                    </CardItem>
                    
                    <CardItem translateZ="0" className="w-full mt-auto">
                      <div className={`w-full h-48 rounded-lg bg-gradient-to-br ${sponsor.color} p-[1px] group-hover/card:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all duration-300`}>
                        <div className="w-full h-full bg-black/80 rounded-lg overflow-hidden relative">
                           <SponsorImage src={sponsor.image} name={sponsor.name} className="w-full h-full" />
                        </div>
                      </div>
                    </CardItem>
                    </CardBody>
                  </CardContainer>
                </div>
              );
            })}
          </div>

          {/* Partner Sponsors */}
          <div className="mb-20">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center text-white mb-10 font-['Inter'] flex items-center justify-center gap-4"
            >
              <span className="h-px w-12 bg-white/20"></span>
              Partner Sponsors
              <span className="h-px w-12 bg-white/20"></span>
            </motion.h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 w-full mx-auto">
              {partnerSponsors.map((sponsor, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05, borderColor: "rgba(230, 25, 51, 0.5)" }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 md:p-6 flex flex-col items-center justify-center hover:bg-white/10 transition-all duration-300 w-full text-center group cursor-pointer relative overflow-hidden"
                >
                  <div className="w-32 h-32 mb-6 rounded-full bg-white/5 flex items-center justify-center overflow-hidden border border-white/10 group-hover:border-[#E61933]/30 transition-colors">
                     <SponsorImage src={sponsor.image} name={sponsor.name} className="w-full h-full" />
                  </div>
                  <h4 className="text-lg font-bold text-white group-hover:text-[#FF33CC] transition-colors mb-2 font-['Inter']">{sponsor.name}</h4>
                  <p className="text-xs text-white/50 uppercase tracking-wider font-['Poppins']">{sponsor.type}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* In-Kind Sponsors */}
          <div className="mb-20">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center text-white mb-10 font-['Inter'] flex items-center justify-center gap-4"
            >
              <span className="h-px w-12 bg-white/20"></span>
              In-Kind Sponsors
              <span className="h-px w-12 bg-white/20"></span>
            </motion.h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 w-full mx-auto">
              {inKindSponsors.map((sponsor, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05, borderColor: "rgba(230, 25, 51, 0.5)" }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 md:p-6 flex flex-col items-center justify-center hover:bg-white/10 transition-all duration-300 w-full text-center group cursor-pointer"
                >
                   <div className="w-28 h-28 mb-6 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden border border-white/10 group-hover:border-[#E61933]/30 transition-colors">
                     <SponsorImage src={sponsor.image} name={sponsor.name} className="w-full h-full" />
                  </div>
                  <h4 className="text-lg font-bold text-white group-hover:text-[#FF33CC] transition-colors mb-2 font-['Inter']">{sponsor.name}</h4>
                  <p className="text-xs text-white/50 uppercase tracking-wider font-['Poppins']">{sponsor.type}</p>
                </motion.div>
              ))}
            </div>
          </div>
          
           {/* Community Partners */}
          <div className="w-full mb-20">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-center text-white mb-16 font-['Inter'] flex items-center justify-center gap-4"
            >
              <span className="h-px w-12 bg-white/20"></span>
              Community Partners
              <span className="h-px w-12 bg-white/20"></span>
            </motion.h3>
            
            <div className="relative flex items-center justify-center overflow-hidden w-full mx-auto py-10">
              <InfiniteMovingCards 
                items={communityPartners.map((partner, idx) => (
                  <CommunityCard key={idx} partner={partner} />
                ))} 
                direction="left" 
                speed="slow"
              />
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SponsorsListPage;
